import { Logo } from '@/components/logo';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import type { LegalLink, LegalSection } from '@/content/legal/privacy';
import { Ionicons } from '@expo/vector-icons';
import { Href, Link, Stack, router } from 'expo-router';
import React from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const isNative = Platform.OS !== 'web';

/**
 * Casca das páginas de texto legal e de atendimento (`/terms`, `/privacidade`,
 * `/suporte`, `/ajuda/recebimentos`): barra de voltar no app, logo, H1 de
 * verdade, a linha de data e o rodapé com "Começar agora" e "Voltar para o
 * início". O texto sai inteiro no HTML estático (nada depende de aba ou de
 * JavaScript para aparecer).
 */
export function LegalPageFrame({
  screenTitle,
  head,
  title,
  meta,
  intro,
  children,
  footerLinks = [],
}: {
  screenTitle: string;
  head: React.ReactNode;
  title: string;
  /** Linha pequena abaixo do H1 (ex.: "Última atualização: ..."). */
  meta?: string;
  intro?: string;
  children: React.ReactNode;
  /** Links extras do rodapé, antes de "Voltar para o início". */
  footerLinks?: { label: string; href: Href }[];
}) {
  const insets = useSafeAreaInsets();

  return (
    <View className="flex-1 bg-background">
      <Stack.Screen
        options={{ title: screenTitle, headerShown: !isNative, headerShadowVisible: true }}
      />
      {head}

      {isNative ? (
        <View className="border-b border-border bg-card" style={{ paddingTop: insets.top }}>
          <View className="min-h-12 flex-row items-center px-4">
            <Pressable onPress={() => router.back()} className="-ml-2 p-2" hitSlop={8}>
              <Ionicons name="arrow-back" size={24} color="#FD5001" />
            </Pressable>
            <ThemedText className="ml-1 font-semibold text-lg">{screenTitle}</ThemedText>
          </View>
        </View>
      ) : null}

      <ScrollView
        keyboardShouldPersistTaps="handled"
        className="flex-1"
        contentContainerStyle={{ paddingBottom: isNative ? insets.bottom + 40 : 40 }}
      >
        <View role="main" className={`mx-auto w-full max-w-4xl px-6 ${isNative ? 'py-8' : 'py-12'}`}>
          <View className="mb-10 items-center">
            <Logo size={48} color="tint" />
            <ThemedText role="heading" aria-level={1} className="mt-6 text-center font-bold text-3xl">
              {title}
            </ThemedText>
            {meta ? (
              <ThemedText className="mt-2 text-center text-sm opacity-60">{meta}</ThemedText>
            ) : null}
            {intro ? (
              <ThemedText className="mt-3 max-w-xl text-center text-base leading-relaxed opacity-70">
                {intro}
              </ThemedText>
            ) : null}
          </View>

          {children}

          <View className="mt-16 items-center border-t border-border pt-8">
            <Link href="/(auth)/register" asChild>
              <Button title="Começar agora" className="px-8" />
            </Link>
            {footerLinks.map((link) => (
              <Link key={String(link.href)} href={link.href} asChild>
                <Pressable className="mt-4" accessibilityRole="link">
                  <ThemedText className="font-medium text-primary">{link.label}</ThemedText>
                </Pressable>
              </Link>
            ))}
            <Link href="/" asChild>
              <Pressable className="mt-4" accessibilityRole="link">
                <ThemedText className="font-medium text-primary">Voltar para o início</ThemedText>
              </Pressable>
            </Link>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

/** Título de seção: H2 de verdade na web e para leitor de tela. */
export function LegalHeading({ children }: { children: React.ReactNode }) {
  return (
    <ThemedText role="heading" aria-level={2} className="font-semibold text-xl text-primary">
      {children}
    </ThemedText>
  );
}

export function LegalParagraph({ children }: { children: React.ReactNode }) {
  return <ThemedText className="text-base leading-relaxed opacity-80">{children}</ThemedText>;
}

export function LegalBullets({ items }: { items: string[] }) {
  return (
    <View className="gap-2">
      {items.map((item) => (
        <View key={item} className="flex-row gap-2">
          <ThemedText className="text-base text-primary">•</ThemedText>
          <ThemedText className="flex-1 text-base leading-relaxed opacity-80">{item}</ThemedText>
        </View>
      ))}
    </View>
  );
}

/** Link de verdade (`<a href>` na web): caminho do site ou URL externa. */
export function LegalLinkLine({ link }: { link: LegalLink }) {
  const external = !link.url.startsWith('/');
  return (
    <Link
      href={link.url as Href}
      {...(external ? { target: '_blank', rel: 'noopener' } : {})}
      className="py-0.5 font-medium text-base text-primary underline"
    >
      {link.label}
    </Link>
  );
}

/** Uma seção inteira a partir do conteúdo puro de `content/legal/*`. */
export function LegalSectionView({ section }: { section: LegalSection }) {
  return (
    <View nativeID={section.id} className="mb-8 gap-3">
      <LegalHeading>{section.title}</LegalHeading>
      {section.paragraphs.slice(0, 1).map((p) => (
        <LegalParagraph key={p}>{p}</LegalParagraph>
      ))}
      {section.items?.length ? <LegalBullets items={section.items} /> : null}
      {section.paragraphs.slice(1).map((p) => (
        <LegalParagraph key={p}>{p}</LegalParagraph>
      ))}
      {section.links?.length ? (
        <View className="gap-1">
          {section.links.map((link) => (
            <LegalLinkLine key={link.url} link={link} />
          ))}
        </View>
      ) : null}
    </View>
  );
}
